import type { IModalActionForm, IModalActionResolver } from "@complex/Actions/Modal/logics/types";
import { CircularProgress, FormControlLabel, Switch, Tooltip } from "@mui/material";
import { StableSwitcher } from "@surfaces/StableSwitcher";
import { useEffect } from "react";
import { type Control, useController, useWatch } from "react-hook-form";
import {
  EnumAppointmentReminderMissingCommunicationPreferenceReason,
  getAppointmentReminderSwitchLabel,
  useAppointmentReminderCandidateCommunicationPreferences,
  useAppointmentReminderCommunication,
  useAppointmentReminderTooltipTitle,
} from "./logics";

export interface IModalActionFormAppointmentReminderProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
}

export const ModalActionFormAppointmentReminder = ({ control }: IModalActionFormAppointmentReminderProps) => {
  const [communicationPreference, availableCommunications, loading, communicationMissingReason, missingContactDetails] =
    useAppointmentReminderCandidateCommunicationPreferences(control);
  const appointmentReminderCommunication = useAppointmentReminderCommunication(
    communicationPreference,
    availableCommunications
  );
  const tooltipTitle = useAppointmentReminderTooltipTitle(
    control,
    communicationPreference,
    appointmentReminderCommunication,
    missingContactDetails
  );
  const hidden = useWatch({
    control,
    name: "status",
    compute: (status) => status !== "todo",
  });
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({ control, name: "shouldScheduleAppointmentReminder", defaultValue: false });
  const disabled = Boolean(tooltipTitle);

  // The reminder would not be scheduled anyway: turning the switch off keeps the form in sync with
  // what is saved, and releases the fields it makes required (start time, address).
  useEffect(() => {
    if (!loading && disabled && value) {
      onChange(false);
    }
  }, [loading, disabled, value, onChange]);

  if (hidden) {
    return null;
  }
  return (
    <FormControlLabel
      control={
        <StableSwitcher active={loading ? "loading" : "done"}>
          <CircularProgress color="primary" size={16} data-switcher="loading" />
          <Tooltip title={tooltipTitle} arrow data-switcher="done">
            <span>
              <Switch
                checked={value}
                onChange={onChange}
                onBlur={onBlur}
                slotProps={{ input: ref }}
                disabled={disabled}
                sx={{ mr: -1 }}
              />
            </span>
          </Tooltip>
        </StableSwitcher>
      }
      label={getAppointmentReminderSwitchLabel(communicationPreference)}
      labelPlacement="start"
      sx={{
        display:
          communicationMissingReason === EnumAppointmentReminderMissingCommunicationPreferenceReason.CANDIDATE_MISSING
            ? "none"
            : undefined,
        justifyContent: "space-between",
      }}
    />
  );
};
